import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { api } from '@/lib/api';import { ArrowLeft, PlayCircle, CheckCircle2, Music2, Clock, KeyRound, Crown, Lock } from 'lucide-react';
import { toast } from 'sonner';
import PlayAlong from '@/components/PlayAlong';
import ShareSongButton from '@/components/ShareSongButton';
import { extractYouTubeId } from '@/lib/useYouTubePlayer';
import TranslatedText from '@/components/TranslatedText';
import ChordChart from '@/components/ChordChart';
import { estimateDifficulty, DIFFICULTY_LABEL_FR, difficultyDelta } from '@/lib/difficultyEstimator';

export default function SongDetail() {
  const { id } = useParams();
  const [song, setSong] = useState(null);
  const [tab, setTab] = useState('chords');

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/songs/${id}`);
      setSong(data);
    })();
  }, [id]);

  const markLearning = async (status) => {
    try {
      await api.post('/progress', { song_id: id, status, minutes_practiced: 10 });
      toast.success(status === 'completed' ? 'Chanson marquée comme terminée !' : 'Ajoutée à ta pratique');
    } catch { toast.error("Impossible d'enregistrer"); }
  };

  if (!song) return <Layout><div className="p-10 text-stone-400">Chargement...</div></Layout>;

  return (
    <Layout>
      <div className="p-6 md:p-10 max-w-5xl">
        <Link to="/library" data-testid="back-library" className="inline-flex items-center gap-2 text-stone-400 hover:text-stone-100 mb-6">
          <ArrowLeft size={16} /> Bibliothèque
        </Link>

        <div className="grid md:grid-cols-12 gap-8 mb-10 rise">
          <div className="md:col-span-5">
            <div className="aspect-square rounded-3xl overflow-hidden border border-stone-800 bg-stone-900">
              {song.cover_url && <img src={song.cover_url} alt={song.title} className="w-full h-full object-cover" />}
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="overline text-orange-400 mb-2">{song.genre} · {song.difficulty}</div>
            <h1 className="font-display text-5xl md:text-6xl leading-none tracking-tight">{song.title}</h1>
            <div className="text-stone-300 text-xl mt-2">{song.artist}</div>
            {(() => {
              const est = estimateDifficulty(song.chords, song.bpm);
              const delta = est.level ? difficultyDelta(est.level, song.difficulty) : 0;
              if (!est.level || delta === 0) return null;
              return (
                <div
                  data-testid="song-difficulty-estimate"
                  title={est.reasons.join(', ')}
                  className={`mt-3 inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${
                    delta > 0
                      ? 'bg-amber-950/40 text-amber-300 border-amber-800/60'
                      : 'bg-emerald-950/40 text-emerald-300 border-emerald-800/60'
                  }`}
                >
                  Estimation auto : {DIFFICULTY_LABEL_FR[est.level]}
                  <span className="opacity-60">· {est.reasons.slice(0, 2).join(' · ')}</span>
                </div>
              );
            })()}
            <p className="text-stone-400 mt-4 max-w-xl">
              {song.description && <TranslatedText text={song.description} cacheKey={`song-desc-${song.id}`} />}
            </p>

            <div className="mt-6 flex flex-wrap gap-4 text-sm text-stone-300">
              {song.duration && <MetaPill icon={Clock} label={song.duration} />}
              {song.key && <MetaPill icon={KeyRound} label={`Tonalité ${song.key}`} />}
              {song.bpm && <MetaPill icon={Music2} label={`${song.bpm} BPM`} />}
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <button data-testid="mark-learning-btn" onClick={() => markLearning('learning')} className="btn-primary flex items-center gap-2">
                <PlayCircle size={18} /> Commencer à apprendre
              </button>
              <button data-testid="mark-completed-btn" onClick={() => markLearning('completed')} className="btn-ghost flex items-center gap-2">
                <CheckCircle2 size={16} /> Marquer terminée
              </button>
              <ShareSongButton song={song} />
            </div>
          </div>
        </div>

        {song.video_url && !(!song.audio_url && !song.locked && extractYouTubeId(song.video_url) && (song.chord_timeline || []).length > 0) && (
          <div className="mb-10 rise">
            <div className="overline text-stone-400 mb-3">Tutoriel vidéo</div>
            <div className="aspect-video rounded-2xl overflow-hidden border border-stone-800 bg-stone-900">
              <iframe
                src={song.video_url}
                title={song.title}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>
        )}

        {song.audio_url && !song.locked && (!song.chord_timeline || song.chord_timeline.length === 0) && (
          <div className="mb-10 rise">
            <div className="overline text-stone-400 mb-3">Audio original</div>
            <div className="card-stone p-4 flex items-center gap-3">
              <audio
                controls
                src={song.audio_url}
                className="w-full h-11"
                data-testid="song-audio-player"
                preload="metadata"
              />
            </div>
            <div className="text-xs text-stone-500 mt-2 italic">Écoute la version originale pour t'imprégner du rythme et de la mélodie.</div>
          </div>
        )}

        {song.locked ? (
          <div className="mb-10 rise">
            <div className="card-stone p-8 text-center border border-orange-800/60">
              <Crown className="mx-auto text-orange-400 mb-3" size={40} />
              <div className="font-display text-3xl mb-2">Chanson Pro</div>
              <p className="text-stone-400 max-w-md mx-auto mb-6">
                Les accords, tablatures et le Play-Along de ce morceau ({song.difficulty}) sont réservés aux membres Pro. Débloque tout avec 7 jours d'essai gratuit.
              </p>
              <Link to="/pro" data-testid="song-pro-cta" className="btn-primary inline-flex items-center gap-2">
                <Crown size={16} /> Passer Pro
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-10 rise">
              <div className="overline text-stone-400 mb-3">Play-Along sync</div>
              <PlayAlong song={song} />
            </div>

            <div className="rise">
              <div className="flex gap-2 mb-4 border-b border-stone-800">
                {['chords','tab','lyrics'].filter(t => (
                  (t === 'chords' && song.chords) || (t === 'tab' && song.tab) || (t === 'lyrics' && song.lyrics)
                )).map(t => (
                  <button
                    key={t}
                    data-testid={`tab-${t}`}
                    onClick={() => setTab(t)}
                    className={`px-4 py-2 text-sm rounded-t-lg border-b-2 transition-colors ${
                      tab === t ? 'border-orange-600 text-stone-100' : 'border-transparent text-stone-400 hover:text-stone-200'
                    }`}
                  >
                    {t === 'chords' ? 'Accords' : t === 'tab' ? 'Tablature' : 'Paroles'}
                  </button>
                ))}
              </div>
              {tab === 'chords' && song.chords ? (
                <div data-testid="chords-view">
                  <ChordChart text={song.chords} timeSignature={song.time_signature || '4/4'} bpm={song.bpm} />
                </div>
              ) : (
                <pre data-testid={`content-${tab}`} className="card-stone p-6 text-stone-200 whitespace-pre-wrap font-mono text-sm leading-7 overflow-x-auto">
                  {tab === 'tab' ? song.tab : song.lyrics}
                </pre>
              )}
              {(song.lyrics_note || tab === 'lyrics') && (
                <div className="mt-3 text-xs text-stone-500 italic">
                  {song.lyrics_note || "Extrait pédagogique. Les paroles complètes restent disponibles sur les sites officiels des ayants droit."}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

function MetaPill({ icon: Icon, label }) {
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-stone-900 border border-stone-800">
      <Icon size={14} className="text-orange-400" />
      <span>{label}</span>
    </div>
  );
}
